import { Box, Stack, Typography, useTheme } from '@mui/material';
import React from 'react';
import FormCounter from '../../../../../shared/FormCounter';
import CustomInputLabel from '../../../../../../layout/styledcomponents/CustomInputLabel';
import { reservationEditFieldLabelSx } from '../../../EditReservation/reservationEditFieldStyles';

type Props = {
    experienceType: string;
    subType?: string;
    visitType?: string;
    startDate: string;
    endDate: string;
    adultCount: number;
    childCount: number;
    maxGuests: number;
    /** Activity experiences: max attendees (`details.quantity`). */
    activityGuestMax?: number;
    overnightGuests?: number;
    overnightMax?: number;
    maxReached: boolean;
    checkIfOvernight: () => boolean;
};

export default function GuestsSection({
    experienceType,
    subType,
    visitType,
    startDate,
    endDate,
    adultCount,
    childCount,
    maxGuests,
    activityGuestMax,
    overnightGuests = 0,
    overnightMax = 0,
    maxReached,
    checkIfOvernight,
}: Props) {
    const isActivity = experienceType === 'activity';
    const isCommercial = experienceType === 'commercial';
    const singleDay = startDate === endDate;
    const personal = visitType === 'personal';

    const label = isCommercial
        ? 'How many attendees?'
        : isActivity || visitType === 'corporate ' || singleDay
          ? 'How many guests will attend for the day?'
          : personal && !singleDay
            ? 'How many guests will stay?'
            : 'How many day guests will attend?';
    const showOvernight = !(
        overnightMax === 0 ||
        subType === 'commercial' ||
        subType === 'office space' ||
        experienceType === 'activity' ||
        experienceType === 'vehicle' ||
        experienceType === 'commercial' ||
        visitType === 'personal' ||
        singleDay
    );

    const maxOvernightReached =
        overnightMax === 0 || overnightGuests >= overnightMax;

    const dayGuestCap =
        !isActivity && !isCommercial && maxGuests > 0 ? maxGuests : undefined;
    const adultsFieldMax =
        dayGuestCap != null
            ? Math.max(0, dayGuestCap - (childCount || 0))
            : undefined;
    const childrenFieldMax =
        dayGuestCap != null
            ? Math.max(0, dayGuestCap - (adultCount || 0))
            : undefined;
    const activityMax =
        activityGuestMax != null && activityGuestMax > 0
            ? activityGuestMax
            : undefined;
    const overnightFieldMax = overnightMax > 0 ? overnightMax : undefined;

    const theme = useTheme();

    return (
        <Stack spacing={1}>
            <Typography variant='body3' sx={reservationEditFieldLabelSx}>
                {label}
                {isCommercial ? (
                    <span style={{ color: 'red' }}>*</span>
                ) : adultCount || childCount ? null : (
                    <span style={{ color: 'red' }}>*</span>
                )}{' '}
                {!isActivity && !isCommercial && (
                    <span
                        style={{
                            fontSize: '10px',
                            color: checkIfOvernight()
                                ? 'red'
                                : theme.palette.midGrey.main,
                        }}
                    >
                        {checkIfOvernight()
                            ? `Max guests reached`
                            : `Max ${
                                  !singleDay && personal
                                      ? `${overnightMax} overnight guests`
                                      : `${maxGuests} guests`
                              }`}
                    </span>
                )}
            </Typography>

            {isCommercial ? (
                <Box display='flex' justifyContent='space-between'>
                    <CustomInputLabel fontWeight='none' htmlFor='guests__input'>
                        Guests
                    </CustomInputLabel>
                    <FormCounter
                        id='guests__input'
                        name='bookingInfo.guests'
                        disabled={false}
                        disableIncrement={maxReached}
                        max={maxGuests > 0 ? maxGuests : undefined}
                    />
                </Box>
            ) : (
                <>
                    <Box display='flex' justifyContent='space-between'>
                        <CustomInputLabel
                            fontWeight='none'
                            htmlFor='adults__input'
                        >
                            {isActivity ? 'Guests' : 'Adults'}
                        </CustomInputLabel>
                        <FormCounter
                            id='adults__input'
                            name={
                                isActivity
                                    ? 'bookingInfo.guests'
                                    : 'bookingInfo.adults'
                            }
                            disabled={false}
                            disableIncrement={maxReached}
                            max={isActivity ? activityMax : adultsFieldMax}
                        />
                    </Box>

                    {!isActivity && (
                        <Box display='flex' justifyContent='space-between'>
                            <CustomInputLabel fontWeight='none'>
                                Children
                            </CustomInputLabel>
                            <FormCounter
                                name='bookingInfo.children'
                                disabled={false}
                                disableIncrement={maxReached}
                                max={childrenFieldMax}
                            />
                        </Box>
                    )}

                    {showOvernight && (
                        <>
                            <Typography
                                variant='body3'
                                sx={reservationEditFieldLabelSx}
                            >
                                How many guests will sleep overnight?{' '}
                                {!singleDay && overnightGuests === 0 && (
                                    <span style={{ color: 'red' }}>*</span>
                                )}{' '}
                                {experienceType === 'activity' ? null : (
                                    <span
                                        style={{
                                            fontSize: '10px',
                                            color: maxOvernightReached
                                                ? 'red'
                                                : theme.palette.midGrey.main,
                                        }}
                                    >
                                        (
                                        {maxOvernightReached
                                            ? 'Max guests reached'
                                            : `Max ${overnightMax} guests`}
                                        )
                                    </span>
                                )}
                            </Typography>
                            <Box display='flex' justifyContent='space-between'>
                                <CustomInputLabel
                                    fontWeight='none'
                                    htmlFor='overnight-guests__input'
                                >
                                    Overnight Guests
                                </CustomInputLabel>
                                <FormCounter
                                    id='overnight-guests__input'
                                    name='bookingInfo.overnightGuests'
                                    disabled={false}
                                    disableIncrement={maxOvernightReached}
                                    max={overnightFieldMax}
                                />
                            </Box>
                        </>
                    )}
                </>
            )}
        </Stack>
    );
}
